import React from 'react'
import * as ProgressPrimitive from '@radix-ui/react-progress'
import { CheckCircle, AlertCircle, X, Loader2 } from 'lucide-react'
import { cn } from '../../../utils/styles'
import { formatFileSize, formatDuration } from '../utils/fileUtils'
import type { UploadProgressProps, UploadProgress } from '../types'

export function UploadProgressBar({
  progress,
  file,
  variant = 'bar',
  showDetails = true,
  className
}: UploadProgressProps) {
  const { percentage, loaded, total, speed, timeRemaining } = progress

  const isCompleted = percentage >= 100
  const hasError = percentage < 0

  if (variant === 'circle') {
    return <ProgressCircle progress={progress} file={file} showDetails={showDetails} className={className} />
  }

  if (variant === 'minimal') {
    return <ProgressMinimal progress={progress} file={file} className={className} />
  }

  return (
    <div className={cn('space-y-2', className)}>
      {/* Header with file info and details */}
      {showDetails && (
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center space-x-2 flex-1 min-w-0">
            {isCompleted ? (
              <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
            ) : hasError ? (
              <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
            ) : (
              <Loader2 className="w-4 h-4 text-blue-500 animate-spin flex-shrink-0" />
            )}

            {file && (
              <span className="font-medium text-gray-700 truncate">
                {file.name}
              </span>
            )}
          </div>

          <div className="flex items-center space-x-3 text-xs text-gray-500">
            {!hasError && (
              <>
                <span>{percentage.toFixed(0)}%</span>
                {total > 0 && (
                  <span>
                    {formatFileSize(loaded)} / {formatFileSize(total)}
                  </span>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Progress bar */}
      <ProgressPrimitive.Root
        className={cn(
          'relative overflow-hidden bg-gray-200 rounded-full w-full h-2',
          hasError && 'bg-red-100'
        )}
        value={Math.max(0, Math.min(100, hasError ? 100 : percentage))}
      >
        <ProgressPrimitive.Indicator
          className={cn(
            'h-full w-full flex-1 transition-transform duration-300 ease-out rounded-full',
            {
              'bg-gradient-to-r from-blue-500 to-blue-600': !isCompleted && !hasError,
              'bg-gradient-to-r from-green-500 to-green-600': isCompleted,
              'bg-gradient-to-r from-red-500 to-red-600': hasError
            }
          )}
          style={{
            transform: `translateX(-${100 - Math.max(0, Math.min(100, hasError ? 100 : percentage))}%)`,
            transition: hasError ? 'none' : undefined
          }}
        />
      </ProgressPrimitive.Root>

      {/* Footer with speed and time remaining */}
      {showDetails && !isCompleted && !hasError && (
        <div className="flex items-center justify-between text-xs text-gray-500">
          <div className="flex items-center space-x-3">
            {speed && speed > 0 && (
              <span>{formatFileSize(speed)}/s</span>
            )}
            {timeRemaining && timeRemaining > 0 && (
              <span>{formatDuration(timeRemaining)} remaining</span>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

// Circular progress variant
function ProgressCircle({
  progress,
  file,
  showDetails,
  className
}: {
  progress: UploadProgress
  file?: File
  showDetails?: boolean
  className?: string
}) {
  const { percentage } = progress
  const radius = 40
  const circumference = 2 * Math.PI * radius
  const strokeDashoffset = circumference - (percentage / 100) * circumference

  const isCompleted = percentage >= 100
  const hasError = percentage < 0

  return (
    <div className={cn('flex items-center space-x-4', className)}>
      {/* Circular progress */}
      <div className="relative flex-shrink-0">
        <svg
          className="w-20 h-20 transform -rotate-90"
          width="80"
          height="80"
        >
          {/* Background circle */}
          <circle
            cx="40"
            cy="40"
            r={radius}
            stroke="currentColor"
            strokeWidth="4"
            fill="none"
            className="text-gray-200"
          />
          {/* Progress circle */}
          <circle
            cx="40"
            cy="40"
            r={radius}
            stroke="currentColor"
            strokeWidth="4"
            fill="none"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            strokeLinecap="round"
            className={cn(
              'transition-all duration-300 ease-out',
              {
                'text-blue-500': !isCompleted && !hasError,
                'text-green-500': isCompleted,
                'text-red-500': hasError
              }
            )}
          />
        </svg>

        {/* Center content */}
        <div className="absolute inset-0 flex items-center justify-center">
          {isCompleted ? (
            <CheckCircle className="w-6 h-6 text-green-500" />
          ) : hasError ? (
            <AlertCircle className="w-6 h-6 text-red-500" />
          ) : (
            <span className="text-sm font-medium text-gray-700">
              {percentage.toFixed(0)}%
            </span>
          )}
        </div>
      </div>

      {/* File details */}
      {showDetails && file && (
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-700 truncate">
            {file.name}
          </p>
          <p className="text-xs text-gray-500">
            {formatFileSize(file.size)}
          </p>
        </div>
      )}
    </div>
  )
}

// Minimal progress variant
function ProgressMinimal({
  progress,
  file,
  className
}: {
  progress: UploadProgress
  file?: File
  className?: string
}) {
  const { percentage } = progress
  const isCompleted = percentage >= 100
  const hasError = percentage < 0

  return (
    <div className={cn('flex items-center space-x-2', className)}>
      {isCompleted ? (
        <CheckCircle className="w-4 h-4 text-green-500" />
      ) : hasError ? (
        <AlertCircle className="w-4 h-4 text-red-500" />
      ) : (
        <div className="w-4 h-4 relative">
          <div className="absolute inset-0 rounded-full border-2 border-gray-200" />
          <div
            className="absolute inset-0 rounded-full border-2 border-blue-500 transition-all duration-300"
            style={{
              clipPath: `conic-gradient(transparent ${percentage * 3.6}deg, black ${percentage * 3.6}deg)`
            }}
          />
        </div>
      )}

      {file && (
        <span className="text-sm text-gray-700">
          {file.name} ({percentage.toFixed(0)}%)
        </span>
      )}
    </div>
  )
}

// Batch progress for multiple files
export interface BatchProgressProps {
  completed: number
  total: number
  currentFile?: File
  currentProgress?: UploadProgress
  className?: string
  showCurrentFile?: boolean
}

export function BatchProgress({
  completed,
  total,
  currentFile,
  currentProgress,
  className,
  showCurrentFile = true
}: BatchProgressProps) {
  const overallPercentage = total > 0 ? (completed / total) * 100 : 0
  const isCompleted = completed >= total

  return (
    <div className={cn('space-y-3', className)}>
      {/* Overall progress */}
      <div className="space-y-2">
        <div className="flex items-center justify-between text-sm">
          <span className="font-medium text-gray-700">
            Overall Progress
          </span>
          <div className="flex items-center space-x-2">
            <span className="text-gray-500">
              {completed} of {total} files
            </span>
            <span className="font-medium">
              {overallPercentage.toFixed(0)}%
            </span>
          </div>
        </div>

        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className={cn(
              'h-full transition-all duration-300 ease-out rounded-full',
              isCompleted
                ? 'bg-gradient-to-r from-green-500 to-green-600'
                : 'bg-gradient-to-r from-blue-500 to-blue-600'
            )}
            style={{ width: `${overallPercentage}%` }}
          />
        </div>
      </div>

      {/* Current file progress */}
      {showCurrentFile && currentFile && currentProgress && !isCompleted && (
        <div className="pt-2 border-t">
          <p className="text-xs text-gray-500 mb-2">Currently uploading:</p>
          <UploadProgressBar
            progress={currentProgress}
            file={currentFile}
            variant="bar"
            showDetails={false}
          />
        </div>
      )}
    </div>
  )
}

// Progress with cancel functionality
export interface CancellableProgressProps extends UploadProgressProps {
  onCancel?: () => void
  cancelLabel?: string
}

export function CancellableProgress({
  onCancel,
  cancelLabel = 'Cancel',
  className,
  ...progressProps
}: CancellableProgressProps) {
  const { progress } = progressProps
  const isCompleted = progress.percentage >= 100
  const hasError = progress.percentage < 0

  return (
    <div className={cn('space-y-2', className)}>
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <UploadProgressBar {...progressProps} showDetails={false} />
        </div>

        {onCancel && !isCompleted && !hasError && (
          <button
            type="button"
            onClick={onCancel}
            className="ml-3 p-1 text-gray-400 hover:text-gray-600 transition-colors"
            aria-label={cancelLabel}
            title={cancelLabel}
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {progressProps.showDetails && (
        <div className="text-xs text-gray-500">
          {isCompleted && 'Upload completed'}
          {hasError && 'Upload failed'}
          {!isCompleted && !hasError && progress.speed && (
            <>
              {formatFileSize(progress.speed)}/s
              {progress.timeRemaining && (
                <> • {formatDuration(progress.timeRemaining)} remaining</>
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}